import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Chip, IconButton, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { useEffect, useState } from 'react';
import type { CreativeItem } from '@/entities/creatives';
import { CreativeItemType, selectCreativeByTuple } from '@/entities/creatives';
import { useAppSelector } from '@/shared/model';
import { CopyButton, Panel, Scroll } from '@/shared/ui';
import { useCreativeDrawerContext } from './CreativeDrawerContext';

const DRAWER_WIDTH = 340;

const TYPE_LABELS: Record<CreativeItemType, string> = {
  [CreativeItemType.Image]: 'Image',
  [CreativeItemType.Video]: 'Video',
  [CreativeItemType.Carousel]: 'Carousel',
  [CreativeItemType.DynamicImage]: 'Dynamic Image',
  [CreativeItemType.DynamicVideo]: 'Dynamic Video',
  [CreativeItemType.Unknown]: 'Unknown',
};

function VideoPlayer({ src }: { readonly src: string }) {
  if (!src) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">
          No video preview available
        </Typography>
      </Box>
    );
  }
  return (
    <Box
      component="video"
      controls
      src={src}
      sx={{
        width: '100%',
        display: 'block',
        borderRadius: 1,
      }}
    />
  );
}

function ImageCarousel({
  assets,
  resetKey,
}: {
  readonly assets: CreativeItem['assets'];
  readonly resetKey: unknown;
}) {
  const images = assets.filter(
    (a): a is typeof a & { url: string } => a.url !== null,
  );
  const [index, setIndex] = useState(0);
  const hasMultipleImages = images.length > 1;

  useEffect(() => {
    setIndex(0);
  }, [resetKey]);

  if (images.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">No images available</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {hasMultipleImages && (
          <IconButton
            size="small"
            onClick={() => setIndex((i) => i - 1)}
            disabled={index === 0}
          >
            <ChevronLeftIcon />
          </IconButton>
        )}
        <Box
          component="img"
          src={images[index].url}
          sx={{
            flex: 1,
            minWidth: 0,
            height: 400,
            display: 'block',
            objectFit: 'contain',
            borderRadius: 1,
          }}
        />
        {hasMultipleImages && (
          <IconButton
            size="small"
            onClick={() => setIndex((i) => i + 1)}
            disabled={index === images.length - 1}
          >
            <ChevronRightIcon />
          </IconButton>
        )}
      </Box>

      {hasMultipleImages && (
        <>
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              textAlign: 'center',
              mt: 0.5,
              color: 'text.secondary',
            }}
          >
            {index + 1} / {images.length}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              gap: 0.5,
              justifyContent: 'center',
              mt: 1,
              flexWrap: 'wrap',
            }}
          >
            {images.map((img, i) => (
              <Box
                key={img.id}
                component="img"
                src={img.url}
                onClick={() => setIndex(i)}
                sx={{
                  width: 40,
                  height: 40,
                  objectFit: 'cover',
                  borderRadius: 0.5,
                  cursor: 'pointer',
                  opacity: i === index ? 1 : 0.45,
                  border: i === index ? '2px solid' : '2px solid transparent',
                  borderColor: i === index ? 'primary.main' : 'transparent',
                }}
              />
            ))}
          </Box>
        </>
      )}
    </Box>
  );
}

function CopyField({
  label,
  value,
}: {
  readonly label: string;
  readonly value: string;
}) {
  return (
    <Box>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mb: 0.5, display: 'block' }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          bgcolor: 'action.hover',
          px: 1.5,
          py: 1,
          borderRadius: 1,
        }}
      >
        <Typography
          variant="body2"
          noWrap
          sx={{ flex: 1, fontFamily: 'monospace', fontSize: '0.8rem' }}
        >
          {value}
        </Typography>
        <CopyButton copyContent={value} />
      </Box>
    </Box>
  );
}

function renderMedia(item: CreativeItem) {
  switch (item.type) {
    case CreativeItemType.DynamicVideo:
    case CreativeItemType.Video:
      return <VideoPlayer src={item.videoPreviewUrl} />;
    case CreativeItemType.Carousel:
    case CreativeItemType.DynamicImage:
    case CreativeItemType.Image:
      return <ImageCarousel assets={item.assets} resetKey={item.adId} />;
    case CreativeItemType.Unknown:
    default:
      return (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            Unknown creative type. We are working to display it.
          </Typography>
        </Box>
      );
  }
}

function DrawerContent({ creative }: { readonly creative: CreativeItem }) {
  const { closeCreativeDrawer } = useCreativeDrawerContext();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          p: 2,
          flexShrink: 0,
        }}
      >
        <Typography
          variant="subtitle1"
          fontWeight={600}
          sx={{
            pr: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {creative.adName}
        </Typography>
        <IconButton
          size="small"
          onClick={closeCreativeDrawer}
          sx={{ mt: -0.5 }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Badges */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          pb: 1.5,
          flexShrink: 0,
        }}
      >
        <Chip label={creative.sourceName} size="small" />
        <Typography variant="caption" color="text.secondary">
          {TYPE_LABELS[creative.type] ?? 'Unknown'}
        </Typography>
      </Box>

      {/* Scrollable middle: media + fields */}
      <Scroll
        slotProps={{
          container: {
            sx: { px: 2, display: 'flex', flexDirection: 'column', gap: 2 },
          },
        }}
      >
        <Box>{renderMedia(creative)}</Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pb: 2 }}>
          <CopyField label="Creative ID" value={creative.creativeId} />
          <CopyField label="Creative name" value={creative.creativeName} />
        </Box>
      </Scroll>
    </Box>
  );
}

export function CreativeDetailsDrawer({
  sx,
}: {
  readonly sx?: SxProps<Theme>;
}) {
  const { currentTuple } = useCreativeDrawerContext();
  const creative = useAppSelector(
    selectCreativeByTuple(
      currentTuple ?? { ad_id: '', ad_name: '', media_source: '' },
    ),
  );
  const open = creative != null;

  return (
    <Panel
      sx={{
        width: open ? DRAWER_WIDTH : 0,
        ml: open ? 1 : 0,
        flexShrink: 0,
        visibility: open ? 'visible' : 'hidden',
        transition: 'width 0.2s ease-in-out, margin 0.2s ease-in-out',
        display: 'flex',
        flexDirection: 'column',

        '& .drawer-content': {
          opacity: open ? 1 : 0,
          transition: open ? 'opacity 0s ease-in-out 0.2s' : 'none',
          height: '100%',
        },
        ...sx,
      }}
    >
      <Box className="drawer-content">
        {creative && <DrawerContent creative={creative} />}
      </Box>
    </Panel>
  );
}
